/**
 * Evaluation 녹화 영상을 에피소드와 재생 카메라로 정규화하는 계약.
 *
 * Owns:
 * - 영상 경로에서 env·episode·camera 식별자를 해석하는 규칙.
 * - 에피소드 결과 분류와 재생·다운로드 가능한 카메라 판정.
 * - 카메라별 다운로드 파일명 구성.
 *
 * Does not own:
 * - 영상 목록 요청, signed URL 발급과 다운로드 전송.
 * - 갤러리 UI와 플레이어 렌더링.
 */
export type RecordedVideo = {
  filename: string | null;
  signed_url: string | null;
  outcome: string | null;
  is_failure: boolean | null;
  failure_reasons: string[];
};

export type RecordedEpisode = {
  key: string;
  envId: number | null;
  episodeIndex: number | null;
  cameras: RecordedVideo[];
  outcome: string | null;
  isFailure: boolean | null;
  failureReasons: string[];
};

export type PlayableCamera = {
  id: string;
  url: string;
  label: string;
  path: string;
};

const SUCCESS_OUTCOMES = new Set(["true_success", "success"]);
const ENV_RE = /(?:^|\/)env_(\d+)/;
const EPISODE_RE = /episode_(\d+)/;

function indexFromFilename(filename: string, pattern: RegExp): number | null {
  const match = filename.match(pattern);
  return match === null ? null : Number.parseInt(match[1], 10);
}

export function groupRecordedEpisodes(videos: readonly RecordedVideo[]): RecordedEpisode[] {
  const groups = new Map<string, RecordedEpisode>();
  for (const video of videos) {
    if (video.filename === null) continue;
    const envId = indexFromFilename(video.filename, ENV_RE);
    const episodeIndex = indexFromFilename(video.filename, EPISODE_RE);
    const key =
      envId !== null || episodeIndex !== null
        ? `${envId ?? "?"}:${episodeIndex ?? "?"}`
        : video.filename;
    const current = groups.get(key) ?? {
      key,
      envId,
      episodeIndex,
      cameras: [],
      outcome: null,
      isFailure: null,
      failureReasons: [],
    };
    current.cameras.push(video);
    current.outcome ??= video.outcome;
    current.isFailure ??= video.is_failure;
    if (current.failureReasons.length === 0) current.failureReasons = video.failure_reasons;
    groups.set(key, current);
  }
  return [...groups.values()].sort(
    (left, right) =>
      (left.envId ?? 0) - (right.envId ?? 0) ||
      (left.episodeIndex ?? 0) - (right.episodeIndex ?? 0),
  );
}

export function isFailureEpisode(episode: RecordedEpisode): boolean {
  return episode.isFailure ?? episode.failureReasons.length > 0;
}

export function isSuccessEpisode(episode: RecordedEpisode): boolean {
  if (episode.isFailure === false) return true;
  return episode.outcome !== null && SUCCESS_OUTCOMES.has(episode.outcome);
}

function episodeSegments(episode: RecordedEpisode): { env: string; index: string } {
  return {
    env: episode.envId === null ? "env_?" : `env_${String(episode.envId).padStart(3, "0")}`,
    index:
      episode.episodeIndex === null
        ? "episode_?"
        : `episode_${String(episode.episodeIndex).padStart(6, "0")}`,
  };
}

export function episodeTitle(episode: RecordedEpisode): string {
  const { env, index } = episodeSegments(episode);
  return `${env} / ${index}`;
}

export function cameraLabel(filename: string): string {
  const parts = filename.split("/");
  const imageIndex = parts.indexOf("observation_image");
  if (imageIndex >= 0 && parts[imageIndex + 1]) return parts[imageIndex + 1];
  return "recording";
}

export function playableCameras(episode: RecordedEpisode): PlayableCamera[] {
  return episode.cameras.flatMap((camera, index) => {
    if (camera.signed_url === null || camera.filename === null) return [];
    return [
      {
        id: `${camera.filename}:${index}`,
        url: camera.signed_url,
        label: cameraLabel(camera.filename),
        path: camera.filename,
      },
    ];
  });
}

/** 경로가 다른 카메라 영상이 겹치지 않도록 에피소드와 카메라를 파일명에 담는다. */
export function recordedVideoFilename(episode: RecordedEpisode, filename: string): string {
  const basename = filename.split("/").pop() ?? filename;
  if (episode.envId === null && episode.episodeIndex === null) {
    return basename;
  }
  const dot = basename.lastIndexOf(".");
  const extension = dot > 0 ? basename.slice(dot) : "";
  const { env, index } = episodeSegments(episode);
  return `${env}_${index}_${cameraLabel(filename)}${extension}`;
}
